import React from 'react';
import {StyleSheet, View} from 'react-native';
import {ThemeProvider} from '@rneui/themed';
import type {Meta, StoryObj} from '@storybook/react-native-web-vite';

import rewardsTheme from '../../theme/rewardsTheme';

import Text from './Text';

const meta = {
  title: 'Components/Primitives/Text',
  component: Text,
  argTypes: {
    variant: {
      control: 'select',
      options: [
        'heading0',
        'heading1',
        'heading2',
        'heading3',
        'heading4',
        'body',
        'body2',
        'caption',
      ],
    },
    align: {
      control: 'select',
      options: ['left', 'center', 'right', 'justify'],
    },
    children: {
      control: 'text',
    },
  },
  decorators: [
    Story => (
      <ThemeProvider theme={rewardsTheme}>
        <View style={styles.container}>
          <Story />
        </View>
      </ThemeProvider>
    ),
  ],
} satisfies Meta<typeof Text>;

export default meta;

type Story = StoryObj<typeof meta>;

export const AllVariants: Story = {
  render: () => (
    <View style={styles.variantsContainer}>
      <Text variant="heading0">heading0 - Large Title</Text>
      <Text variant="heading1">heading1 - Title</Text>
      <Text variant="heading2">heading2 - Subtitle</Text>
      <Text variant="heading3">heading3 - Section Header</Text>
      <Text variant="heading4">heading4 - Small Header</Text>
      <Text variant="body">body - Regular text for paragraphs</Text>
      <Text variant="body2">body2 - Smaller body text</Text>
      <Text variant="caption">caption - Small captions and hints</Text>
    </View>
  ),
};

export const Heading0: Story = {
  args: {
    variant: 'heading0',
    children: 'Heading 0 Example',
  },
};

export const Heading1: Story = {
  args: {
    variant: 'heading1',
    children: 'Heading 1 Example',
  },
};

export const Heading2: Story = {
  args: {
    variant: 'heading2',
    children: 'Heading 2 Example',
  },
};

export const Heading3: Story = {
  args: {
    variant: 'heading3',
    children: 'Heading 3 Example',
  },
};

export const Heading4: Story = {
  args: {
    variant: 'heading4',
    children: 'Heading 4 Example',
  },
};

export const Body: Story = {
  args: {
    variant: 'body',
    children: 'This is regular body text used for paragraphs and main content.',
  },
};

export const Body2: Story = {
  args: {
    variant: 'body2',
    children: 'This is body2 text, slightly smaller than regular body text.',
  },
};

export const Caption: Story = {
  args: {
    variant: 'caption',
    children: 'This is caption text for hints and small labels.',
  },
};

export const TextAlignment: Story = {
  render: () => (
    <View style={styles.variantsContainer}>
      <Text variant="body" align="left">
        Left aligned text
      </Text>
      <Text variant="body" align="center">
        Center aligned text
      </Text>
      <Text variant="body" align="right">
        Right aligned text
      </Text>
      <Text variant="body" align="justify">
        Justified text with enough content to demonstrate the alignment. Lorem ipsum dolor sit amet,
        consectetur adipiscing elit.
      </Text>
    </View>
  ),
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  variantsContainer: {
    gap: 12,
  },
});
